import { Button, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded"
import {
    Content,
    Main,
    PropertyBar,
    QuestionLead,
    QuestionTitle
} from "./styled"
import { ReportSummaryCard } from "./parts/ReportSummaryCard"
import { MobileLayout } from "./utils"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import {
    ImageViewerOverlay,
    useImageViewerOverlay
} from "@/pages/OperationInfoPage/parts/ImageViewerOverlay"
import { LostItemReport } from "@/hooks/useLostItemReport"
import { useTranslation } from "@/hooks/useTranslation"

interface Props {
    reports: LostItemReport[]
    /**
     * この画面を開いてから廃棄物として処理したか。
     * 廃棄は報告レコードを作らないため `reports` からは判定できない。
     */
    hasDisposed?: boolean
    listingName?: string
    deletingId?: string
    layout: MobileLayout
    /** 清掃詳細から単独で開いた場合。次へ進む導線が完了報告ではなく詳細へ戻る。 */
    standalone?: boolean
    onReport: () => void
    onDelete: (report: LostItemReport) => void
    onNext: () => void
    onClose: () => void
}

/**
 * 忘れ物確認の入口。清掃完了フローで必ず通る（スキップ不可）。
 *
 * 未報告なら「忘れ物はありましたか？」の2択、1件以上報告済みなら報告内容の要約と
 * 「他にも忘れ物はありますか？」を出す。報告は1件ずつ永続化されているため、
 * 画面をリロードしてもここに復元される。
 *
 * 廃棄（`hasDisposed`）も報告済みと同じ扱いにする。廃棄はレコードを作らないので、
 * これが無いと廃棄後に初回と同じ「忘れ物はありましたか？」に戻ってしまい、
 * 事実どおり「あった」を選ぶと廃棄フローを回り続けて抜けられなくなる。
 */
export const EntryView = ({
    reports,
    hasDisposed = false,
    listingName,
    deletingId,
    layout,
    standalone = false,
    onReport,
    onDelete,
    onNext,
    onClose
}: Props) => {
    const { t } = useTranslation()
    const hasReports = reports.length > 0
    // 報告したか廃棄したか、いずれかで「忘れ物の処理を1周した」とみなす。
    const hasHandled = hasReports || hasDisposed
    // 単独で開いた時は完了報告に続かないので、次へ進む文言を詳細に戻る意味にする。
    const nextLabel = standalone
        ? t("清掃詳細に戻る")
        : hasHandled
          ? t("完了報告へ進む")
          : t("なかった・次へ進む")

    const { openedImage, openImageViewer, closeImageViewer } =
        useImageViewerOverlay()

    return (
        <Main style={layout.rootProps.style}>
            <Header {...layout.headerProps}>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">
                            {t("忘れ物の確認")}
                        </Typography>
                    }
                    onNext={onClose}
                    nextIcon={<CloseIcon />}
                />
            </Header>

            <div {...layout.bodyProps}>
                {listingName && (
                    <PropertyBar>
                        <ApartmentRoundedIcon style={{ fontSize: 18 }} />
                        <span>{listingName}</span>
                    </PropertyBar>
                )}

                <Content>
                    <div>
                        <QuestionTitle>
                            {hasHandled
                                ? t("他にも忘れ物はありますか？")
                                : t("忘れ物はありましたか？")}
                        </QuestionTitle>
                        <QuestionLead
                            css={css`
                                margin-top: 8px;
                            `}
                        >
                            {hasHandled
                                ? t("なければ、そのまま次へ進んでください。")
                                : t(
                                      "完了報告に進む前に、必ず確認してください。"
                                  )}
                        </QuestionLead>
                    </div>

                    {reports.map(report => (
                        <ReportSummaryCard
                            key={report.id}
                            report={report}
                            deleting={deletingId === report.id}
                            onOpenPhoto={image =>
                                openImageViewer({
                                    id: image.id,
                                    imageUrl: image.url
                                })
                            }
                            onDelete={onDelete}
                        />
                    ))}
                </Content>
            </div>

            <ReportFooter {...layout.footerProps}>
                <Button
                    variant="filled"
                    color="primary"
                    fullWidth
                    onClick={onReport}
                >
                    {hasHandled ? t("もう一件報告する") : t("あった・報告する")}
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onNext}
                    css={css`
                        margin-top: 8px;
                    `}
                >
                    {nextLabel}
                </Button>
            </ReportFooter>

            <ImageViewerOverlay
                openedImage={openedImage}
                onClose={closeImageViewer}
            />
        </Main>
    )
}
